import React, { useEffect, useMemo } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import BaseForm from '../../../../components/shared/form-controls/base-form';
import FormDateSelector from '../../../../components/shared/nextgen/form/form-date-selector';
import { FormDropdown } from '../../../../components/shared/nextgen/form/form-dropdown';
import { FormInput } from '../../../../components/shared/form-controls/form-input';
import { getAllEmployees } from '../../../../features/employees/short';
import { canSeeOtherVacations } from '../../../../features/profile/entityPermissions';
import { createVacation } from '../../../../features/vacation/slice';
import { Vacation, VacationState } from '../../../../features/vacation/types';
import { useAppDispatch, useAppSelector } from '../../../../hooks/hooks';
import { Lx } from '../../../../i18n/consts';
import { NAVIGATION_ROUTES } from '../../../../routes/NavigationRoute';

interface CreateVacationLocationState {
    employeeId?: number;
    employeeName?: string;
    dateFrom?: string;
    dateTo?: string;
}

interface CreateVacationFormValues {
    employee: number;
    period: string[];
    message?: string;
    notifyAboutStart: number;
    notifyInAdvance: number;
}

const NOTIFY_ABOUT_START_TEAMMATES_ONLY = 0;
const NOTIFY_ABOUT_START_ALL_EMPLOYEES = 1;
const VACATION_DATE_FORMAT = 'YYYY-MM-DD';

export default function CreateVacationPage() {
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as CreateVacationLocationState | undefined;
    const employees = useAppSelector((selector) => selector.rEmployees);
    const profile = useAppSelector((selector) => selector.profile);
    const canCreateForOtherEmployees = canSeeOtherVacations(profile);

    useEffect(() => {
        if (canCreateForOtherEmployees && employees.length === 0) {
            dispatch(getAllEmployees());
        }
    }, [canCreateForOtherEmployees, dispatch, employees.length]);

    const selectedEmployee = useMemo(() => {
        const fallbackEmployee = profile.id
            ? employees.find((employee) => employee.id === profile.id) ?? { id: profile.id, name: profile.name, isDefault: false }
            : employees[0];

        if (canCreateForOtherEmployees && state?.employeeId) {
            return employees.find((employee) => employee.id === state.employeeId);
        }

        return canCreateForOtherEmployees
            ? employees[0]
            : fallbackEmployee;
    }, [canCreateForOtherEmployees, employees, profile.id, profile.name, state?.employeeId]);

    const minVacationDate = useMemo(() => moment().add(1, 'day').startOf('day'), []);

    const initialValues = useMemo<CreateVacationFormValues | null>(() => {
        if (!selectedEmployee) {
            return null;
        }

        const fallbackDate = minVacationDate.format(VACATION_DATE_FORMAT);
        const stateDateFrom = state?.dateFrom?.trim() ? moment(state.dateFrom, VACATION_DATE_FORMAT) : null;
        const stateDateTo = state?.dateTo?.trim() ? moment(state.dateTo, VACATION_DATE_FORMAT) : null;
        const dateFromMoment = stateDateFrom?.isAfter(moment(), 'day') ? stateDateFrom : minVacationDate;
        const dateToMoment = stateDateTo?.isSameOrAfter(dateFromMoment, 'day') ? stateDateTo : dateFromMoment;
        const dateFrom = dateFromMoment.format(VACATION_DATE_FORMAT) || fallbackDate;
        const dateTo = dateToMoment.format(VACATION_DATE_FORMAT);

        return {
            employee: selectedEmployee.id,
            period: [dateFrom, dateTo],
            message: '',
            notifyAboutStart: NOTIFY_ABOUT_START_TEAMMATES_ONLY,
            notifyInAdvance: 0,
        };
    }, [minVacationDate, selectedEmployee, state?.dateFrom, state?.dateTo]);

    const validationSchema: Yup.Schema<CreateVacationFormValues> = useMemo(() => Yup.object().shape({
        employee: Yup.number()
            .required(intl.formatMessage({ id: Lx.Vacation.INVALID_EMPLOYEE_ID })),
        period: Yup.array()
            .of(Yup.string().required())
            .length(2)
            .required()
            .test(
                'vacation-date-order',
                intl.formatMessage({ id: Lx.Vacation.INVALID_DATE_FROM }),
                (value) => {
                    if (!value || value.length < 2) {
                        return false;
                    }

                    return moment(value[0], 'YYYY-MM-DD').isSameOrBefore(moment(value[1], 'YYYY-MM-DD'), 'day');
                }
            ),
        message: Yup.string().max(240).optional(),
        notifyAboutStart: Yup.number().oneOf([
            NOTIFY_ABOUT_START_TEAMMATES_ONLY,
            NOTIFY_ABOUT_START_ALL_EMPLOYEES,
        ]).required(),
        notifyInAdvance: Yup.number().oneOf([0, 1, 2, 3, 4, 5]).required(),
    }), [intl]);

    const employeeOptions = useMemo(() => {
        if (!selectedEmployee) {
            return [];
        }

        return [{
            id: selectedEmployee.id,
            name: state?.employeeName?.trim() || selectedEmployee.name,
        }];
    }, [selectedEmployee, state?.employeeName]);

    const notifyAboutStartOptions = useMemo(() => ([
        {
            id: NOTIFY_ABOUT_START_TEAMMATES_ONLY,
            name: intl.formatMessage({ id: Lx.Vacation.NOTIFY_ABOUT_START_TEAMMATES_ONLY }),
        },
        {
            id: NOTIFY_ABOUT_START_ALL_EMPLOYEES,
            name: intl.formatMessage({ id: Lx.Vacation.NOTIFY_ABOUT_START_ALL_EMPLOYEES }),
        },
    ]), [intl]);

    const notifyInAdvanceOptions = useMemo(() => ([
        { id: 0, name: intl.formatMessage({ id: Lx.Vacation.NOTIFY_IN_ADVANCE_DISABLED }) },
        { id: 1, name: intl.formatMessage({ id: Lx.Vacation.NOTIFY_IN_ADVANCE_ONE_DAY }) },
        { id: 2, name: intl.formatMessage({ id: Lx.Vacation.NOTIFY_IN_ADVANCE_TWO_DAYS }) },
        { id: 3, name: intl.formatMessage({ id: Lx.Vacation.NOTIFY_IN_ADVANCE_THREE_DAYS }) },
        { id: 4, name: intl.formatMessage({ id: Lx.Vacation.NOTIFY_IN_ADVANCE_FOUR_DAYS }) },
        { id: 5, name: intl.formatMessage({ id: Lx.Vacation.NOTIFY_IN_ADVANCE_FIVE_DAYS }) },
    ]), [intl]);

    const handleSubmit = async (values: CreateVacationFormValues) => {
        const employeeId = canCreateForOtherEmployees
            ? values.employee
            : profile.id;

        if (!employeeId) {
            return;
        }

        const payload: Vacation = {
            id: 0,
            employeeId,
            dateFrom: values.period[0],
            dateTo: values.period[1],
            comment: values.message,
            state: VacationState.WaitApprove,
            notifyNDaysInAdvance: values.notifyInAdvance,
            notifyAll: values.notifyAboutStart === NOTIFY_ABOUT_START_ALL_EMPLOYEES,
        };

        await dispatch(createVacation(payload)).unwrap();
        navigate(NAVIGATION_ROUTES.VACATIONREPORT);
    };

    if (!initialValues) {
        return null;
    }

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            validateOnBlur={true}
            validateOnChange={true}
            enableReinitialize
            onSubmit={handleSubmit}
        >
            <Form>
                <BaseForm
                    contentGap={24}
                    formType='create'
                    formHeader={Lx.Vacation.CREATE_NEW_VACATION}
                    savingTitle={intl.formatMessage({ id: Lx.General.VACATION })}
                    onClose={() => navigate(NAVIGATION_ROUTES.VACATIONREPORT)}
                    onAfterSave={() => navigate(NAVIGATION_ROUTES.VACATIONREPORT)}
                    allowPristineSubmit
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%', maxWidth: '520px' }}>
                        <FormDateSelector
                            label={intl.formatMessage({ id: Lx.General.DATE })}
                            name={'period'}
                            range
                            required
                            minDate={minVacationDate}
                            hidePresets
                        />
                        <FormDropdown
                            label={intl.formatMessage({ id: Lx.General.EMPLOYEE })}
                            name={'employee'}
                            items={employeeOptions}
                            initialValue={employeeOptions[0]}
                            required
                            selectProps={{ disabled: true }}
                        />
                        <FormInput
                            label={intl.formatMessage({ id: Lx.Vacation.MESSAGE })}
                            name={'message'}
                            placeholder={intl.formatMessage({ id: Lx.Vacation.MESSAGE })}
                            required={false}
                            style={{ width: '100%', maxWidth: '520px' }}
                        />
                        <FormDropdown
                            label={intl.formatMessage({ id: Lx.Vacation.NOTIFY_ABOUT_START })}
                            name={'notifyAboutStart'}
                            items={notifyAboutStartOptions}
                            initialValue={notifyAboutStartOptions[0]}
                            required
                        />
                        <FormDropdown
                            label={intl.formatMessage({ id: Lx.Vacation.NOTIFY_IN_ADVANCE })}
                            name={'notifyInAdvance'}
                            items={notifyInAdvanceOptions}
                            initialValue={notifyInAdvanceOptions[0]}
                            required
                        />
                    </div>
                </BaseForm>
            </Form>
        </Formik>
    );
}
